import { loadThree } from "./display.js";

/**
 * Welcome / login screen — the first thing a player sees.
 *
 * A full-screen interactive 3D scene (Three.js): a point-cloud globe you can
 * drag to spin, deal routes arcing between cities, a skyline of company
 * towers rising around it, a starfield, and camera parallax that follows the
 * pointer. Over it, the sign-in card:
 *
 *   - Sign in / Create account / Continue with Google: Firebase Auth. The
 *     server hands over the Firebase web config (GET /api/meta, from
 *     GES_FIREBASE_CONFIG); the Firebase SDK loads from Google's CDN only
 *     then. Signed in, every request carries the ID token (api.js) and the
 *     player's own game loads. The first sign-in keeps the company played as
 *     a guest (POST /api/account/claim-guest). With no config, the buttons
 *     stay disabled and say accounts are coming.
 *   - Play as guest: a game kept for this browser; sign in later to keep it.
 *
 * The choice is remembered for this browser tab (sessionStorage), so the
 * screen shows again on a new visit. Respects prefers-reduced-motion, and
 * falls back to an animated CSS backdrop when WebGL or the CDN is missing,
 * and in Lite display mode (three.js is only fetched in 3D mode).
 */

const FB_ERRORS = {
  "auth/invalid-credential": "That email and password don't match an account.",
  "auth/wrong-password": "That email and password don't match an account.",
  "auth/user-not-found": "No account with that email. Create one instead.",
  "auth/email-already-in-use": "An account already uses that email. Sign in instead.",
  "auth/weak-password": "Pick a password of at least 6 characters.",
  "auth/invalid-email": "That doesn't look like an email address.",
  "auth/popup-closed-by-user": "The Google window was closed before signing in.",
  "auth/network-request-failed": "No connection to Google. Check your internet and try again.",
  "auth/unauthorized-domain": "This website isn't authorised in Firebase yet (add it under Authentication → Settings → Authorized domains).",
};
const fbError = (e) => new Error(FB_ERRORS[e && e.code] || (e && e.message) || "Sign-in failed.");

const GESAuth = {
  mode: null,              // "guest" | "account" | null
  accountsEnabled: false,  // true once Firebase is configured and loaded
  user: null,
  fb: null,
  async signIn(email, password) {
    try { await GESAuth.fb.signInWithEmailAndPassword(email, password); } catch (e) { throw fbError(e); }
    await accountEntered();
  },
  async signUp(email, password) {
    try { await GESAuth.fb.createUserWithEmailAndPassword(email, password); } catch (e) { throw fbError(e); }
    await accountEntered();
  },
  async signInWithGoogle() {
    const provider = new window.firebase.auth.GoogleAuthProvider();
    try { await GESAuth.fb.signInWithPopup(provider); }
    catch (e) {
      if (e && (e.code === "auth/popup-blocked" || e.code === "auth/operation-not-supported-in-this-environment")) {
        await GESAuth.fb.signInWithRedirect(provider); // phones that block pop-ups: back here after Google
        return;
      }
      throw fbError(e);
    }
    await accountEntered();
  },
  async signOut() {
    try { if (GESAuth.fb) await GESAuth.fb.signOut(); } catch (e) { /* signed out locally anyway */ }
    window.gesAuthToken = null;
    try { sessionStorage.removeItem("ges.session"); } catch (e) { /* private mode */ }
    window.location.reload();
  },
};
window.GESAuth = GESAuth;

/**
 * Signed in: load the account's own game. An account with no company yet founds one (name, industry,
 * country on the founding screen). If this browser played a guest company, the player chooses first:
 * bring it into the account, or start fresh. Nothing moves without asking.
 */
async function accountEntered() {
  const u = GESAuth.fb && GESAuth.fb.currentUser;
  if (u) window.gesAuthToken = await u.getIdToken();
  // the server must accept the sign-in too; if it refuses, say why instead of quietly loading the guest game
  try {
    const me = await (await fetch("/api/account/me")).json();
    if (me.players && !me.signed_in) {
      throw new Error(`Google sign-in worked, but the game server couldn't confirm it${me.auth_error ? ` (${me.auth_error})` : ""}. Please try again in a minute, or play as a guest.`);
    }
  } catch (e) {
    if (e && e.message && e.message.startsWith("Google sign-in worked")) throw e;
    /* the check itself failed (offline): carry on as before */
  }
  try { sessionStorage.setItem("ges.session", "account"); } catch (e) { /* private mode */ }
  let guest = null;
  try {
    const r = await fetch("/api/account/guest-company");
    if (r.ok) guest = (await r.json()).company;
  } catch (e) { /* no guest game to offer: the account just starts fresh */ }
  if (guest && await askKeepGuest(guest)) {
    try { await fetch("/api/account/claim-guest", { method: "POST" }); } catch (e) { /* starts fresh */ }
  }
  window.location.reload();
}

/** "Keep your guest company or start a new one?" Resolves true to keep it. */
function askKeepGuest(company) {
  return new Promise((resolve) => {
    const el = document.createElement("div");
    el.className = "modal-backdrop guest-start";
    el.style.zIndex = "calc(var(--z-auth) + 1)";
    el.innerHTML = `<div class="modal" role="dialog" aria-label="Which company do you want in your account?" style="width:min(520px, 92vw);">
        <div class="sx-eyebrow">Signed in</div><h3>Which company do you want in your account?</h3>
        <p style="color:var(--text-mid); font-size:13px;">You played a company as a guest on this device. Bring it into your account, or found a new one.</p>
        <div class="gs-options">
          <button type="button" class="gs-opt" data-keep="0"><b>🏁 Start a new company</b><span>Choose your company's name, industry and country.</span></button>
          <button type="button" class="gs-opt" data-keep="1"><b>▶ Keep ${authEsc(company.name)}</b><span>${authEsc(company.industry)} · month ${company.current_month}</span></button>
        </div></div>`;
    document.body.appendChild(el);
    el.addEventListener("click", (e) => {
      const b = e.target.closest("[data-keep]");
      if (!b) return;
      el.querySelectorAll("[data-keep]").forEach((x) => { x.disabled = true; });
      resolve(b.dataset.keep === "1");
    });
  });
}

/** The founding form starts with the player's name from their Google account (they can change it). */
function prefillFounder() {
  const name = GESAuth.user && GESAuth.user.displayName;
  if (!name) return;
  ["f-founder", "qs-founder"].forEach((id) => {
    const el = document.getElementById(id);
    if (el && !el.value) el.value = name;
  });
}

/** Firebase, if the server is configured for it (api.js starts it, since it loads first). */
async function initAccounts() {
  const fb = window.gesFirebase ? await window.gesFirebase.catch(() => null) : null;
  if (!fb) return;
  GESAuth.fb = fb;
  GESAuth.accountsEnabled = true;
  GESAuth.user = fb.currentUser;
  fb.onIdTokenChanged((u) => { GESAuth.user = u; });
  const back = await fb.getRedirectResult().catch(() => null); // returning from Google on a phone
  if (back && back.user) {
    let stored = null;
    try { stored = sessionStorage.getItem("ges.session"); } catch (e) { /* private mode */ }
    if (stored !== "account") {
      try { await accountEntered(); } catch (e) { window.alert(e.message); } // the server refused the sign-in: say why
    }
  }
}

const AUTH_KEY = "ges.session";

function authStored() {
  try { return sessionStorage.getItem(AUTH_KEY); } catch (e) { return null; }
}
function authStore(v) {
  try { sessionStorage.setItem(AUTH_KEY, v); } catch (e) { /* private mode: just don't remember */ }
}

// ------------------------------------------------------------------ 3D scene

function authScene(canvas, T) {
  if (!T) return null;
  let renderer;
  try {
    renderer = new T.WebGLRenderer({ canvas, antialias: true, alpha: true });
  } catch (e) { return null; }
  const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  const scene = new T.Scene();
  scene.fog = new T.FogExp2(0x05060c, 0.035);
  const camera = new T.PerspectiveCamera(55, 1, 0.1, 200);
  camera.position.set(0, 2.2, 16);

  const world = new T.Group();
  scene.add(world);

  // Globe: a Fibonacci point sphere plus a faint wireframe shell.
  const R = 4;
  const N = 2600;
  const gpos = new Float32Array(N * 3);
  const gcol = new Float32Array(N * 3);
  const cA = new T.Color(0x7ee8fa), cB = new T.Color(0xa78bfa);
  for (let i = 0; i < N; i++) {
    const y = 1 - (i / (N - 1)) * 2, r = Math.sqrt(1 - y * y), th = i * 2.399963;
    gpos.set([Math.cos(th) * r * R, y * R, Math.sin(th) * r * R], i * 3);
    const c = cA.clone().lerp(cB, (y + 1) / 2);
    gcol.set([c.r, c.g, c.b], i * 3);
  }
  const gGeo = new T.BufferGeometry();
  gGeo.setAttribute("position", new T.BufferAttribute(gpos, 3));
  gGeo.setAttribute("color", new T.BufferAttribute(gcol, 3));
  const globe = new T.Points(gGeo, new T.PointsMaterial({ size: 0.055, vertexColors: true, transparent: true, opacity: 0.9 }));
  world.add(globe);
  world.add(new T.Mesh(new T.SphereGeometry(R * 0.985, 32, 24),
    new T.MeshBasicMaterial({ color: 0x0d1530, transparent: true, opacity: 0.55 })));
  world.add(new T.Mesh(new T.SphereGeometry(R * 1.002, 24, 16),
    new T.MeshBasicMaterial({ color: 0x3fa9c4, wireframe: true, transparent: true, opacity: 0.07 })));

  // Deal routes: arcs between random points with a travelling spark.
  const onSphere = () => {
    const u = Math.random() * 2 - 1, t = Math.random() * Math.PI * 2, s = Math.sqrt(1 - u * u);
    return new T.Vector3(s * Math.cos(t), u, s * Math.sin(t)).multiplyScalar(R);
  };
  const arcs = [];
  for (let i = 0; i < 14; i++) {
    const a = onSphere(), b = onSphere();
    const mid = a.clone().add(b).multiplyScalar(0.5);
    mid.setLength(R + 1.2 + a.distanceTo(b) * 0.35);
    const curve = new T.QuadraticBezierCurve3(a, mid, b);
    const line = new T.Line(new T.BufferGeometry().setFromPoints(curve.getPoints(60)),
      new T.LineBasicMaterial({ color: i % 3 === 0 ? 0xfbbf24 : 0x7ee8fa, transparent: true, opacity: 0.35 }));
    const spark = new T.Mesh(new T.SphereGeometry(0.07, 8, 8), new T.MeshBasicMaterial({ color: 0xffffff }));
    world.add(line, spark);
    arcs.push({ curve, spark, t: Math.random(), speed: 0.12 + Math.random() * 0.18 });
  }

  // Skyline: a ring of company towers that rise on load.
  const towers = [];
  const towerGroup = new T.Group();
  scene.add(towerGroup);
  const palette = [0x2b6f86, 0x3f97b3, 0x6d5bd0, 0x8b5cf6, 0xb8862b];
  // Only behind and beside the globe (z <= ~2), so nothing looms in front
  // of the camera or the sign-in card.
  for (let i = 0; i < 64; i++) {
    const ang = Math.PI * (0.92 + (i / 63) * 1.16), dist = 7 + Math.random() * 4;
    const h = 0.5 + Math.pow(Math.random(), 2.2) * 4.2;
    const geo = new T.BoxGeometry(0.45, 1, 0.45);
    geo.translate(0, 0.5, 0);
    const mesh = new T.Mesh(geo, new T.MeshStandardMaterial({ color: palette[i % palette.length], emissive: 0x0a1a2a,
                                                               metalness: 0.4, roughness: 0.35, transparent: true, opacity: 0.92 }));
    mesh.position.set(Math.cos(ang) * dist, -4.6, Math.sin(ang) * dist);
    mesh.scale.y = 0.001;
    towerGroup.add(mesh);
    towers.push({ mesh, h, delay: Math.random() * 1.6 });
  }
  const floor = new T.Mesh(new T.RingGeometry(7.5, 13.5, 64), new T.MeshBasicMaterial({ color: 0x7ee8fa, transparent: true, opacity: 0.05, side: T.DoubleSide }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -4.6;
  towerGroup.add(floor);
  // three r155+ lighting: intensities are x pi of the old (r128) values, and
  // decay 0 + a 0.75 factor approximates the old linear falloff at camera range.
  scene.add(new T.AmbientLight(0x8899cc, 0.55 * Math.PI));
  const key = new T.PointLight(0x7ee8fa, 1.4 * Math.PI * 0.75, 60, 0);
  key.position.set(6, 8, 10);
  scene.add(key);
  const rim = new T.PointLight(0xa78bfa, 1.1 * Math.PI * 0.75, 60, 0);
  rim.position.set(-10, -2, -6);
  scene.add(rim);

  // Starfield.
  const S = 1400, spos = new Float32Array(S * 3);
  for (let i = 0; i < S; i++) spos.set([(Math.random() - 0.5) * 140, (Math.random() - 0.5) * 90, -20 - Math.random() * 80], i * 3);
  const sGeo = new T.BufferGeometry();
  sGeo.setAttribute("position", new T.BufferAttribute(spos, 3));
  const stars = new T.Points(sGeo, new T.PointsMaterial({ color: 0xaab8ff, size: 0.12, transparent: true, opacity: 0.7 }));
  scene.add(stars);

  // Interaction: pointer parallax, drag to spin the globe (with inertia).
  const pointer = { x: 0, y: 0 };
  let dragging = false, lastX = 0, lastY = 0, velX = 0, velY = 0;
  const onMove = (e) => {
    const p = e.touches ? e.touches[0] : e;
    pointer.x = (p.clientX / window.innerWidth) * 2 - 1;
    pointer.y = (p.clientY / window.innerHeight) * 2 - 1;
    if (dragging) {
      velY = (p.clientX - lastX) * 0.005;
      velX = (p.clientY - lastY) * 0.003;
      lastX = p.clientX; lastY = p.clientY;
    }
  };
  const onDown = (e) => {
    const p = e.touches ? e.touches[0] : e;
    dragging = true; lastX = p.clientX; lastY = p.clientY;
    canvas.classList.add("grabbing");
  };
  const onUp = () => { dragging = false; canvas.classList.remove("grabbing"); };
  window.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerdown", onDown);
  window.addEventListener("pointerup", onUp);

  const resize = () => {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // Wide screens: push the scene right so the card has room on the left.
    // Phones: lift and shrink it so it shows above the card.
    world.position.x = w > 900 ? 4.2 : 0;
    world.position.y = w > 900 ? 0 : 3.4;
    world.scale.setScalar(w > 900 ? 1 : 0.62);
    towerGroup.position.x = world.position.x;
    towerGroup.position.y = w > 900 ? 0 : 3.2;
    towerGroup.scale.setScalar(w > 900 ? 1 : 0.62);
    camera.updateProjectionMatrix();
  };
  resize();
  window.addEventListener("resize", resize);

  let last = performance.now(), t = 0;
  let running = true, intro = 0;
  const tick = () => {
    if (!running) return;
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now; t += dt;
    intro = Math.min(1, intro + dt * (reduced ? 4 : 0.45));
    const ease = 1 - Math.pow(1 - intro, 3);
    camera.position.z = 22 - 6 * ease;
    camera.position.x += ((pointer.x * 1.4) - camera.position.x) * 0.04;
    camera.position.y += ((2.2 - pointer.y * 1.0) - camera.position.y) * 0.04;
    camera.lookAt(world.position.x * 0.6, world.position.y * 0.55, 0);
    if (!dragging) { velY *= 0.95; velX *= 0.92; }
    world.rotation.y += (reduced ? 0.0005 : 0.0018) + velY;
    world.rotation.x = Math.max(-0.6, Math.min(0.6, world.rotation.x + velX));
    towerGroup.rotation.y -= reduced ? 0 : 0.0009;
    for (const a of arcs) {
      a.t = (a.t + dt * a.speed * (reduced ? 0.2 : 1)) % 1;
      a.spark.position.copy(a.curve.getPoint(a.t));
    }
    for (const tw of towers) {
      const k = Math.max(0, Math.min(1, (t - tw.delay) / 1.4));
      tw.mesh.scale.y = Math.max(0.001, tw.h * (1 - Math.pow(1 - k, 3)) * (1 + Math.sin(t * 0.8 + tw.delay * 4) * 0.03));
    }
    stars.rotation.z += 0.00005;
    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  return {
    stop() {
      running = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("resize", resize);
      renderer.dispose();
    },
  };
}

// ------------------------------------------------------------------ the card

function authCardHtml() {
  const off = GESAuth.accountsEnabled ? "" : "disabled";
  return `
    <div class="auth-card ${GESAuth.accountsEnabled ? "" : "accounts-off"}">
      <div class="auth-brand"><span class="auth-logo">◆</span> GES</div>
      <h1 class="auth-title">Global Enterprise<br/>Simulator</h1>
      <p class="auth-tag">Found a company. Launch products, close deals, buy rivals and climb the world ranking.</p>

      <div class="auth-tabs" role="tablist">
        <button type="button" class="auth-tab on" data-auth-tab="signin">Sign in</button>
        <button type="button" class="auth-tab" data-auth-tab="signup">Create account</button>
      </div>
      <form class="auth-form" id="auth-form" autocomplete="on">
        <input type="email" id="auth-email" placeholder="Email" autocomplete="email" ${off}/>
        <input type="password" id="auth-pass" placeholder="Password" autocomplete="current-password" ${off}/>
        <button type="submit" class="primary" id="auth-submit" ${off}>Sign in</button>
        <button type="button" class="auth-google" id="auth-google" ${off}><span>G</span> Continue with Google</button>
        ${GESAuth.accountsEnabled ? "" : `<div class="auth-soon">Accounts with cloud saves are coming soon. For now, play as a guest.</div>`}
        <div class="auth-error hidden" id="auth-error"></div>
      </form>

      <div class="auth-or"><span>or</span></div>
      <button type="button" class="auth-guest" id="auth-guest">Play as guest <b>→</b></button>
      <a class="auth-about" href="/about.html">What is GES? About the game</a>
    </div>

    <div class="auth-guest-panel hidden" id="auth-guest-panel" role="dialog" aria-labelledby="auth-guest-title">
      <div class="auth-card">
        <div class="sx-eyebrow">Before you start</div>
        <h2 id="auth-guest-title">Playing as a guest</h2>
        <ul class="auth-rules">
          <li><b>Your game is kept for this browser.</b> Clearing the browser's data, or playing on another device, starts you from zero.</li>
          <li><b>Sign in with Google any time</b> to keep this company in your account and play it anywhere.</li>
          <li><b>Want a copy?</b> In the game, open <em>Save/Load</em> and use <em>Export to File</em>. You can import it later.</li>
          <li><b>Start over any time</b> with <em>Save/Load → Reset save</em>.</li>
        </ul>
        <label class="auth-ack"><input type="checkbox" id="auth-ack"/> I understand a guest game lives in this browser until I sign in</label>
        <div class="auth-guest-actions">
          <button type="button" id="auth-guest-back">Back</button>
          <button type="button" class="primary" id="auth-guest-go" disabled>Start as guest</button>
        </div>
      </div>
    </div>`;
}

function showAuthScreen({ force = false } = {}) {
  if (!force && GESAuth.user) { GESAuth.mode = "account"; authStore("account"); authAccountChip(); prefillFounder(); return; }
  if (!force && authStored() === "guest") { GESAuth.mode = "guest"; authGuestChip(); return; }
  const root = document.createElement("div");
  root.id = "auth-screen";
  root.innerHTML = `
    <canvas id="auth-canvas" aria-hidden="true"></canvas>
    <div class="auth-fallback" aria-hidden="true"></div>
    <div class="auth-hint">Drag the globe</div>
    ${authCardHtml()}`;
  document.body.appendChild(root);
  document.body.classList.add("auth-open");
  // CSS backdrop first; the WebGL globe replaces it once three.js has loaded (3D mode only).
  root.classList.add("no-webgl");
  let scene = null;
  loadThree().then((T) => {
    if (!root.isConnected || root.classList.contains("leaving")) return;
    scene = authScene(root.querySelector("#auth-canvas"), T);
    if (scene) root.classList.remove("no-webgl");
  });

  const $a = (s) => root.querySelector(s);
  let tab = "signin";
  root.querySelectorAll("[data-auth-tab]").forEach((b) => b.addEventListener("click", () => {
    tab = b.dataset.authTab;
    root.querySelectorAll("[data-auth-tab]").forEach((x) => x.classList.toggle("on", x === b));
    $a("#auth-submit").textContent = tab === "signin" ? "Sign in" : "Create account";
    $a("#auth-pass").autocomplete = tab === "signin" ? "current-password" : "new-password";
  }));
  const fail = (e) => { const el = $a("#auth-error"); el.textContent = e.message; el.classList.remove("hidden"); };
  const enter = (mode) => {
    GESAuth.mode = mode;
    authStore(mode);
    root.classList.add("leaving");
    setTimeout(() => {
      scene && scene.stop(); root.remove(); document.body.classList.remove("auth-open"); authGuestChip();
      if (mode === "guest") guestStartChoice();
    }, 650);
  };
  $a("#auth-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    try {
      const fn = tab === "signin" ? GESAuth.signIn : GESAuth.signUp;
      await fn($a("#auth-email").value, $a("#auth-pass").value);  // reloads into the account's game
    } catch (err) { fail(err); }
  });
  $a("#auth-google").addEventListener("click", async () => {
    try { await GESAuth.signInWithGoogle(); } catch (err) { fail(err); }
  });
  $a("#auth-guest").addEventListener("click", () => $a("#auth-guest-panel").classList.remove("hidden"));
  $a("#auth-guest-back").addEventListener("click", () => $a("#auth-guest-panel").classList.add("hidden"));
  $a("#auth-ack").addEventListener("change", (e) => { $a("#auth-guest-go").disabled = !e.target.checked; });
  $a("#auth-guest-go").addEventListener("click", () => enter("guest"));
}

/**
 * A guest picks how to start: a game already on this machine may not be
 * theirs, so they choose to continue it or found their own company (name
 * and industry on the founding screen). With no company yet, founding opens
 * anyway, so this only asks when there's something to continue.
 */
async function guestStartChoice() {
  let company = null;
  try {
    const r = await fetch("/api/company");
    if (r.ok) company = await r.json();
  } catch (e) { return; }
  if (!company) return; // boot() already shows the founding screen
  const el = document.createElement("div");
  el.className = "modal-backdrop guest-start";
  el.innerHTML = `<div class="modal" role="dialog" aria-label="How do you want to start?" style="width:min(520px, 92vw);">
      <div class="sx-eyebrow">Playing as a guest</div><h3>How do you want to start?</h3>
      <p style="color:var(--text-mid); font-size:13px;">There's already a company on this device. Guest games aren't saved to an account, so pick what you want to play.</p>
      <div class="gs-options">
        <button type="button" class="gs-opt" data-gs="new"><b>🏁 Start a new company</b><span>Choose your company's name and industry. The game on this device is replaced.</span></button>
        <button type="button" class="gs-opt" data-gs="continue"><b>▶ Continue ${authEsc(company.name)}</b><span>${authEsc(company.industry)} · month ${company.current_month}</span></button>
      </div></div>`;
  document.body.appendChild(el);
  el.addEventListener("click", async (e) => {
    const b = e.target.closest("[data-gs]");
    if (!b) return;
    if (b.dataset.gs === "continue") { el.remove(); return; }
    b.disabled = true;
    try { await fetch("/api/new-game", { method: "POST" }); } catch (err) { /* reload shows what's there */ }
    window.location.reload();
  });
}

const authEsc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** A small "Guest" chip in the game's top bar that re-explains guest mode. */
function authGuestChip() {
  if (GESAuth.mode !== "guest" || document.getElementById("guest-chip")) return;
  const chip = document.createElement("button");
  chip.id = "guest-chip";
  chip.type = "button";
  chip.className = "guest-chip";
  chip.title = "Guest mode: progress isn't saved to an account";
  chip.innerHTML = `<i></i>Guest`;
  chip.addEventListener("click", () => showAuthScreen({ force: true }));
  document.body.appendChild(chip);
}

/** Signed in: your picture or initial in the corner; tap for your account and to sign out. */
function authAccountChip() {
  const u = GESAuth.user;
  if (!u || document.getElementById("account-chip")) return;
  const name = u.displayName || u.email || "Your account";
  const chip = document.createElement("div");
  chip.id = "account-chip";
  chip.className = "guest-chip account-chip";
  chip.innerHTML = `<button type="button" class="ac-face" title="${authEsc(name)}">${u.photoURL
    ? `<img src="${authEsc(u.photoURL)}" alt="" referrerpolicy="no-referrer">` : authEsc(name.slice(0, 1).toUpperCase())}</button>
    <div class="ac-menu hidden"><b>${authEsc(name)}</b><span>Your company is saved to this account.</span>
      <button type="button" class="ac-out">Sign out</button></div>`;
  chip.querySelector(".ac-face").addEventListener("click", () => chip.querySelector(".ac-menu").classList.toggle("hidden"));
  chip.querySelector(".ac-out").addEventListener("click", () => GESAuth.signOut());
  document.body.appendChild(chip);
}

initAccounts().then(() => showAuthScreen());
